import React, { FunctionComponent, ReactNode, useState } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { ChevronIcon, ChevronIconDirection } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChevronIcon'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { AccessibilityRolesNative } from '@lyrahealth-inc/ui-core-crossplatform/src/constants'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

export interface NpTestPlanMeasureSectionProps {
  title: string
  children: ReactNode
  testIdName: string
}

const Section = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['16px'],
}))

const Header = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['16px'],
  alignSelf: 'flex-start',
  paddingVertical: spacing['8px'],
}))

const Groups = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['32px'],
}))

/**
 * Not `CollapsibleSection`: its card border and header rule clash with the group cards, and its
 * measured-height animation clips content that changes while open.
 */
export const NpTestPlanMeasureSection: FunctionComponent<NpTestPlanMeasureSectionProps> = ({
  title,
  children,
  testIdName,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme() as ThemeType
  const [isOpen, setIsOpen] = useState(true)

  return (
    <Section testID={tID(testIdName)}>
      <Header
        testID={tID(`${testIdName}-toggle`)}
        onPress={() => setIsOpen(!isOpen)}
        accessibilityRole={AccessibilityRolesNative.BUTTON}
        aria-expanded={isOpen}
        accessibilityLabel={formatMessage(
          {
            defaultMessage: '{title}, {isOpen, select, true {collapse} other {expand}}',
            description: 'Announced for the control that shows or hides a group of test measures',
          },
          { title, isOpen: String(isOpen) },
        )}
      >
        <ChevronIcon
          size={20}
          fillColor={colors.textPrimary}
          direction={isOpen ? ChevronIconDirection.UP : ChevronIconDirection.DOWN}
        />
        <BodyText bold size={BodyTextSize.LARGE} text={title} />
      </Header>
      {isOpen && <Groups>{children}</Groups>}
    </Section>
  )
}
